import Axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { userLogout } from '../actions/userActions'
import { ErrorTypes } from './constants'
import { getUserPreference } from './common'
import NetworkError from './error'

const baServerUrl = import.meta.env.VITE_BA_SERVER_URL

// shangjia server
export const commonServerInstance = Axios.create({
  baseURL: baServerUrl,
  timeout: 60000,
  headers: {
    common: {
      Authorization: `Bearer ${getUserPreference().authToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }
})

export const downloadInstance = Axios.create({
  baseURL: baServerUrl,
  timeout: 0,
  responseType: 'blob',
  headers: {
    common: {
      Authorization: `Bearer ${getUserPreference().authToken}`,
      'Content-Type': 'application/json'
    }
  }
})

/*
 * Both instances declare `Content-Type: application/json` as a default header,
 * which is right for the JSON endpoints that make up most of the API surface.
 * Since axios 1.x that default silently breaks every FormData upload: its
 * `transformRequest` reads the declared content type and, when it says JSON,
 * runs the FormData through `formDataToJSON()` — the file never leaves the
 * browser and the server gets `{"file":{}}` instead of a multipart body.
 * axios 0.21 had no such branch; its xhr adapter just dropped the header for
 * FormData, so every call site relied on that without saying so.
 *
 * Re-declaring the content type per request would work, but only for the call
 * sites someone remembers to update. Doing it here covers the ones that exist
 * and the ones written later. The value carries no boundary on purpose —
 * axios appends the real one while serializing.
 */
const useMultipartForFormData = (config: InternalAxiosRequestConfig) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.setContentType('multipart/form-data')
  }
  return config
}

commonServerInstance.interceptors.request.use(useMultipartForFormData)
downloadInstance.interceptors.request.use(useMultipartForFormData)

const unifiedResponseHandler = (response: AxiosResponse<any>) => {
  // 2xx
  switch (response.data.code) {
    case 200:
      return response
    default:
      throw new NetworkError(
        ErrorTypes.JSON,
        response.data.code,
        response.data.message
      )
  }
}

const unifiedDownloadResponseHandler = async (error: any) => {
  const res = error.response
  if (!res) return unifiedErrHandler(error)

  const contentType = res.headers?.['content-type']

  if (res.data instanceof Blob && contentType?.includes('application/json')) {
    try {
      const text = await res.data.text()
      const json = JSON.parse(text)
      res.data = json
    } catch (e) {
      return unifiedErrHandler(error)
    }
  }

  return unifiedErrHandler(error)
}

const unifiedErrHandler = (err: any) => {
  // outside 2xx
  let error
  if (!err?.response?.data?.code) {
    throw new NetworkError(
      ErrorTypes.JSON,
      422,
      'Network Error',
      'Please contact administrators to resolve this issue!'
    )
  }

  const {
    response: {
      data: { code }
    }
  } = err
  switch (code) {
    case 401000:
    case 401002:
      console.log('Login Token Expired! ' + getUserPreference().authToken)
      userLogout()
      break
    default:
      break
  }
  error = new NetworkError(
    ErrorTypes.HTML,
    err.response.data.code,
    err.response.data?.message ?? 'Network Error',
    err.response.data
  )

  throw error
}

commonServerInstance.interceptors.response.use(
  unifiedResponseHandler,
  unifiedErrHandler
)

downloadInstance.interceptors.response.use(response => {
  const contentType = response.headers['content-type']

  if (contentType !== 'application/json') {
    return response
  } else {
    throw new Error('File not found or failed downloading')
  }
}, unifiedDownloadResponseHandler)
